import '../te-spacing';
import '../te-text';
import { html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { booleanConverter } from '../common/converters';
import { theme } from '../common/theme';
import { IconName } from '../te-icon';
import { TeButtonBase } from './te-button-base';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary';

/** Button Component */
@customElement('te-button')
export class TeButton extends TeButtonBase {
  static styles = [
    ...TeButtonBase.styles,
    css`
      .rounded {
        padding: 0 16px;
        border-radius: 20px;
        justify-content: center;
      }
    `,
  ];

  /** Pre-styled button theme */
  @property({ type: String }) variant?: ButtonVariant;
  /** Button has fully rounded edges */
  @property({ type: Boolean, converter: booleanConverter }) rounded = false;
  /** Primary text value */
  @property({ type: String }) text?: string;
  /** Color for text and icon */
  @property({ type: String }) textColor?: string;
  /** Icon name from icon pack */
  @property({ type: String }) icon?: IconName;
  /** Size of the icon */
  @property({ type: Number }) iconSize?: number;

  getBackgroundColor() {
    const orDisabled = (color: string) => {
      return this.disabled ? theme.colors.shade3.toString() : color;
    };

    switch (this.variant) {
      case 'primary':
        return orDisabled(theme.colors.primary.v0.toString());
      case 'secondary':
        return orDisabled(theme.colors.shade5.toString());
      case 'tertiary':
        return 'transparent';
      default:
        return orDisabled(this.backgroundColor || theme.colors.primary.v0.toString());
    }
  }

  getTextColor() {
    if (this.disabled && this.variant === 'tertiary') {
      // Disabled tertiary
      return theme.colors.shade3.toString();
    } else if (this.variant === 'tertiary') {
      // Tertiary
      return theme.colors.primary.v0.toString();
    } else if (this.variant === undefined && this.textColor) {
      // Custom color, no variant
      return this.textColor;
    } else {
      return theme.colors.shade0.toString();
    }
  }

  renderIcon() {
    if (!this.icon) return;
    // Rounded /w no text, set wrapper to 1 x 1
    const iconWrapperSize = this.rounded && !this.text ? 1 : 16;
    const iconSize = this.iconSize || (this.rounded ? 12 : 16);
    return html`
      <te-icon
        name=${this.icon}
        size=${iconSize}
        wrapperSize=${iconWrapperSize}
        color=${this.getTextColor()}
      ></te-icon>
    `;
  }

  renderText() {
    if (!this.text) return;
    return html`
      <te-spacing size=${this.icon ? 8 : 0}></te-spacing>
      <te-text size="14" weight="500" color=${this.getTextColor()} display="block" truncate>
        ${this.text}
      </te-text>
    `;
  }

  getButtonClass() {
    return this.rounded ? 'rounded' : '';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-button': TeButton;
  }
}
